import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Download, Pencil } from "lucide-react";
import { api } from "../../lib/api";
import { useToast } from "../../lib/toast";
import { Card } from "../../components/ui/Card";
import { Button } from "../../components/ui/Button";
import { Badge } from "../../components/ui/Badge";

interface SlaLine {
  id: number;
  releaseNumber: string;
  slaNumber: string;
  slaLineItem: number;
  materialNumber: string;
  materialDescription: string;
  deliveryDate: string;
  originalQty: number;
  unit: string;
}

/** Firm SLA Change Request — queries already-CONFIRMED SLA lines
 * (ConfirmationStatus="C" in the source design) and lets the supplier
 * request a change against them, reusing the same Split/Submit flow. */
export function FirmSlaResult() {
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const toast = useToast();
  const navigate = useNavigate();
  const query = useQuery({ queryKey: ["sla-lines", "firm"], queryFn: () => api.get<{ rows: SlaLine[] }>("/sla/lines?alreadyConfirmed=true") });
  const rows = query.data?.rows ?? [];

  function changeSelected() {
    if (!selected.size) return toast("Select at least one confirmed SLA line first", "error");
    sessionStorage.setItem("sla-split-ids", JSON.stringify([...selected]));
    sessionStorage.setItem("sla-split-source", "firm_change");
    navigate("/sla-split");
  }

  return (
    <Card>
      <div className="p-4 border-b border-slate-100 dark:border-white/10 font-semibold text-sm">Confirmed SLA Release Lines — request a change</div>
      <div className="overflow-x-auto">
        <table className="data w-full text-xs">
          <thead className="bg-slate-50 dark:bg-white/5 text-slate-500">
            <tr>
              <th className="p-3 text-left">
                <input type="checkbox" onChange={(e) => setSelected(e.target.checked ? new Set(rows.map((r) => r.id)) : new Set())} />
              </th>
              <th className="p-3 text-left">Release Number</th>
              <th className="p-3 text-left">SLA Number</th>
              <th className="p-3 text-left">Status</th>
              <th className="p-3 text-left">Material</th>
              <th className="p-3 text-left">Confirmed Delivery Date</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-100 dark:border-white/5">
                <td className="p-3">
                  <input
                    type="checkbox"
                    checked={selected.has(r.id)}
                    onChange={() =>
                      setSelected((prev) => {
                        const next = new Set(prev);
                        next.has(r.id) ? next.delete(r.id) : next.add(r.id);
                        return next;
                      })
                    }
                  />
                </td>
                <td className="p-3">{r.releaseNumber}</td>
                <td className="p-3">
                  {r.slaNumber}-{r.slaLineItem}
                </td>
                <td className="p-3">
                  <Badge tone="green">Confirmed</Badge>
                </td>
                <td className="p-3">
                  {r.materialNumber} — {r.materialDescription}
                </td>
                <td className="p-3">{r.deliveryDate}</td>
              </tr>
            ))}
            {!rows.length && (
              <tr>
                <td colSpan={6} className="p-8 text-center text-slate-400">
                  No confirmed SLA lines available for change.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="p-4 flex justify-end gap-2 border-t border-slate-100 dark:border-white/10">
        <Button variant="outline" icon={Download} onClick={() => api.download("/sla/export/firm-change", "Firm_SLA_Change.xlsx")}>
          Export to Excel
        </Button>
        <Button icon={Pencil} disabled={!selected.size} onClick={changeSelected}>
          Change
        </Button>
      </div>
    </Card>
  );
}
